/**
 * WizardShell - Main wizard container with stepper and step routing
 * Manages wizard navigation and displays current step
 */

import React from 'react';
import { useWizard } from '../../contexts/WizardContext';
import { WelcomeScreen } from './WelcomeScreen';
import { TemplateSelectScreen } from './TemplateSelectScreen';
import { CopySelectScreen } from './CopySelectScreen';
import { ScratchDetailsScreen } from './ScratchDetailsScreen';
import './WizardShell.css';

function BOMUploadScreen() {
  return (
    <div className="wizard-placeholder" data-testid="wizard-bom-upload">
      <h2>BOM Import</h2>
      <p>Upload your Bill of Materials (Coming in SoW-117)</p>
    </div>
  );
}

function ReviewScreen() {
  return (
    <div className="wizard-placeholder" data-testid="wizard-review">
      <h2>Review & Create</h2>
      <p>Review your FMEA before creating</p>
    </div>
  );
}

function CreatingScreen() {
  return (
    <div className="wizard-placeholder" data-testid="wizard-creating">
      <h2>Creating your FMEA...</h2>
      <div className="spinner" />
    </div>
  );
}

const steps = ['Select Path', 'Configure', 'Review', 'Create'];

function getStepIndex(currentStep: string): number {
  switch (currentStep) {
    case 'welcome':
      return 0;
    case 'template-select':
    case 'bom-upload':
    case 'scratch-details':
    case 'copy-select':
      return 1;
    case 'review':
      return 2;
    case 'creating':
      return 3;
    default:
      return 0;
  }
}

export function WizardShell() {
  const { state, cancelWizard } = useWizard();

  if (!state.isActive) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Close wizard if clicking the backdrop (not the container)
    if (e.target === e.currentTarget) {
      cancelWizard();
    }
  };

  const renderStep = () => {
    switch (state.currentStep) {
      case 'welcome':
        return <WelcomeScreen />;
      case 'template-select':
        return <TemplateSelectScreen />;
      case 'bom-upload':
        return <BOMUploadScreen />;
      case 'scratch-details':
        return <ScratchDetailsScreen />;
      case 'copy-select':
        return <CopySelectScreen />;
      case 'review':
        return <ReviewScreen />;
      case 'creating':
        return <CreatingScreen />;
      default:
        return <WelcomeScreen />;
    }
  };

  const currentStepIndex = getStepIndex(state.currentStep);

  return (
    <div className="wizard-shell" data-testid="wizard-shell" onClick={handleBackdropClick}>
      <div className="wizard-container">
        {/* Close button */}
        <button
          className="wizard-close"
          onClick={cancelWizard}
          data-testid="wizard-close-button"
          aria-label="Close wizard"
        >
          ×
        </button>

        {/* Progress stepper */}
        {state.currentStep !== 'welcome' && (
          <div className="wizard-stepper" data-testid="wizard-stepper">
            {steps.map((label, index) => (
              <div
                key={label}
                className={`stepper-step ${
                  index === currentStepIndex
                    ? 'active'
                    : index < currentStepIndex
                      ? 'completed'
                      : ''
                }`}
                data-testid={`stepper-step-${index}`}
              >
                <div className="stepper-number">{index < currentStepIndex ? '✓' : index + 1}</div>
                <div className="stepper-label">{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Current step content */}
        <div className="wizard-step-content">{renderStep()}</div>
      </div>
    </div>
  );
}
